import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Chip,
  ButtonGroup,
} from "@mui/material";
import { styled } from "@mui/system";
import { Link, useNavigate } from "react-router-dom";
import {
  ChatMessageEventViewModel,
  MessageEventType,
  OrderStatus,
} from "../../configuration/api/apiSchema";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PaymentIcon from "@mui/icons-material/Payment";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneIcon from "@mui/icons-material/Done";
import { useDispatch } from "react-redux";
import { updateHandymanProposal } from "../../pages/Orders/actions";

const EventPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(3),
  backgroundColor: "#ffffff",
  maxWidth: "70%",
  margin: "0 auto",
  position: "relative",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  border: "1px solid rgba(0,0,0,0.08)",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
}));

interface ChatMessageEventProps {
  event: ChatMessageEventViewModel;
  roleId?: string;
  orderStatus?: OrderStatus;
}

const ChatMessageEvent: React.FC<ChatMessageEventProps> = ({ event, roleId, orderStatus }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getEventIcon = () => {
    switch (event.eventType) {
      /* case MessageEventType.OrderProposalReceived:
        return <LocalOfferIcon sx={{ fontSize: 28 }} color="primary" />; */
      case MessageEventType.OrderCompleted:
        return <CheckCircleIcon sx={{ fontSize: 28 }} color="success" />;
      case MessageEventType.PaymentReceived:
        return <PaymentIcon sx={{ fontSize: 28 }} color="primary" />;
      default:
        return <EventIcon sx={{ fontSize: 28 }} color="action" />;
    }
  };

  const getEventTitle = () => {
    if (event.title) return event.title;

    switch (event.eventType) {
      /* case MessageEventType.OrderProposalReceived:
        return "New Order Proposal"; */
      case MessageEventType.OrderCompleted:
        return "Order Completed";
      case MessageEventType.PaymentReceived:
        return "Payment Received";
      default:
        return "Event";
    }
  };

  const getEventColor = () => {
    switch (event.eventType) {
      /* case MessageEventType.OrderProposalReceived:
        return "rgba(33, 150, 243, 0.04)"; // lighter blue */
      case MessageEventType.OrderCompleted:
        return "rgba(76, 175, 80, 0.04)"; // lighter green
      case MessageEventType.PaymentReceived:
        return "rgba(156, 39, 176, 0.04)"; // lighter purple
      default:
        return "rgba(0, 0, 0, 0.02)"; // very light gray
    }
  };

  const handleAcceptProposal = () => {
    if (event.orderProposalId) {
      dispatch(updateHandymanProposal(event.orderProposalId, "Confirmed") as any);
    }
  };

  const handleRejectProposal = () => {
    if (event.orderProposalId) {
      dispatch(updateHandymanProposal(event.orderProposalId, "Rejected") as any);
    }
  };

  const renderQuickActions = () => {
    if (!roleId) return null;

    switch (event.eventType) {
      /* case MessageEventType.OrderProposalReceived:
        if (roleId === "customer" && orderStatus === OrderStatus.New) {
          return (
            <ButtonGroup size="small" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<DoneIcon />}
                onClick={handleAcceptProposal}
              >
                Accept
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleRejectProposal}
              >
                Reject
              </Button>
            </ButtonGroup>
          );
        }
        break; */
      
      case MessageEventType.OrderCompleted:
        if (roleId === "customer" && orderStatus === OrderStatus.InProgress) {
          return (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={() => navigate(`/orders/${event.orderId}/complete`)}
              sx={{ mt: 2 }}
            >
              Mark as Complete
            </Button>
          );
        }
        break;
    }

    return null;
  };

  return (
    <Box
      sx={{
        position: "relative",
        my: 4,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <EventPaper
        elevation={0}
        sx={{ backgroundColor: getEventColor() }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          position="relative"
          zIndex={1}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: "white",
              mb: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "2px solid",
              borderColor: (theme) => 
                event.eventType === MessageEventType.OrderCompleted
                  ? theme.palette.success.main
                  : theme.palette.primary.main,
            }}
          >
            {getEventIcon()}
          </Box>
          
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{
              fontWeight: 600,
              color: (theme) =>
                event.eventType === MessageEventType.OrderCompleted
                  ? theme.palette.success.main
                  : theme.palette.primary.main,
            }}
          >
            {getEventTitle()}
          </Typography>
          
          {event.content && (
            <Typography 
              variant="body1" 
              align="center" 
              sx={{ 
                mb: 3,
                color: 'text.secondary',
                maxWidth: '90%',
                lineHeight: 1.5,
              }}
            >
              {event.content}
            </Typography>
          )}
          
          {event.orderId && (
            <Button
              component={Link}
              to={`/orders/${event.orderId}`}
              variant="outlined"
              size="medium"
              sx={{ 
                mb: 2,
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              View Order #{event.orderId.substring(0, 8)}
            </Button>
          )}
          
          {renderQuickActions()}
        </Box>
      </EventPaper>
    </Box>
  );
};

export default ChatMessageEvent; 